import { useMemo } from 'react';
import styled from 'styled-components';
import { changeColorAlpha } from '@elfsight/lib-utils';
import { formatDate } from '@elfsight/widget-ui';
import { useBreakpoints } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import processString from 'react-process-string';
import {
  classnamePrefix,
  MOBILE_WIDTH_EXTRA_SMALL,
  TABLET_WIDTH
} from '../../../../../constants';
import { ReadMore } from '../../../common/read-more';
import { Actions } from './Actions';
import { User } from './User';

export const Info = ({
  data: {
    name,
    release_time: time,
    description,
    stats: { plays: viewsCount },
    metadata: { connections },
    link,
    user
  } = {
    name: '',
    created_time: '',
    description: '',
    stats: {
      plays: 0
    },
    metadata: {
      connections: {
        likes: {
          total: 0
        },
        comments: {
          total: 0
        },
        available_albums: {
          total: 0
        }
      }
    },
    link: ''
  },
  elements = ['title', 'date', 'numbers', 'shareButton', 'description'],
  showShare = true,
  popup = false
}) => {
  const { likes, comments, available_albums: collections } = connections;

  const showShareButtonAction =
    !elements.includes('user') && elements.includes('shareButton');

  const { backgroundColor, textColor, lang } = useSettings();
  const [breakpoints, ref] = useBreakpoints(
    TABLET_WIDTH,
    MOBILE_WIDTH_EXTRA_SMALL
  );

  const isTablet = breakpoints[TABLET_WIDTH];
  const isExtraSmallMobile = breakpoints[MOBILE_WIDTH_EXTRA_SMALL];

  const noPopupTabletSize = !popup && isTablet;

  const locale = lang ?? 'en';

  const formattedDate = useMemo(
    () => formatDate({ locale, date: new Date(time) }),
    [time, locale]
  );

  const processStringConfig = [
    {
      regex: /(http|https):\/\/(\S+)\.([a-z]{2,}?)(.*?)( |,|$|\.)/gim,
      fn: (key, result) => (
        <span key={key}>
          <Link
            target="_blank"
            rel="nofollow noreferrer"
            href={`${result[1]}://${result[2]}.${result[3]}${result[4]}`}
          >
            {result[2]}.{result[3]}
            {result[4]}
          </Link>
          {result[5]}
        </span>
      )
    },
    {
      regex: /(\S+)\.([a-z]{2,}?)(.*?)( |,|$|\.)/gim,
      fn: (key, result) => (
        <span key={key}>
          <Link
            target="_blank"
            href={`http://${result[1]}.${result[2]}${result[3]}`}
            rel="nofollow noreferrer"
          >
            {result[1]}.{result[2]}
            {result[3]}
          </Link>
          {result[4]}
        </span>
      )
    }
  ];

  return (
    <Component
      ref={ref}
      _isInPopup={popup}
      _isExtraSmallMobile={isExtraSmallMobile}
      _noPopupTabletSize={noPopupTabletSize}
      _hasElements={!!elements.length}
    >
      {elements.includes('title') && (
        <Name
          _textColor={textColor}
          _isInPopup={popup}
          _fontSize={isTablet ? '14px' : '24px'}
          _lineHeight={isTablet ? '20px' : '32px'}
        >
          {name}
        </Name>
      )}

      {elements.includes('date') && (
        <Time
          _isInPopup={popup}
          _noPopupTabletSize={noPopupTabletSize}
          _textColor={textColor}
        >
          {formattedDate}
        </Time>
      )}

      {elements.includes('user') && (
        <User share={showShare} data={user} link={link} />
      )}

      <Actions
        data={{
          viewsCount,
          likesCount: likes ? likes.total : 0,
          collectionsCount: collections ? collections.total : 0,
          commentsCount: comments ? comments.total : 0,
          link
        }}
        numbers={elements.includes('numbers')}
        share={showShareButtonAction}
        popup={popup}
        mobile={breakpoints[TABLET_WIDTH]}
      />

      {elements.includes('description') && description && (
        <Description
          _textColor={textColor}
          _isInPopup={popup}
          _noPopupTabletSize={noPopupTabletSize}
        >
          <StyledReadMore
            lines={3}
            button="Read More..."
            color={!popup ? backgroundColor : '#fff'}
            _noPopupTabletSize={noPopupTabletSize}
          >
            <Text key={description}>
              {processString(processStringConfig)(description)}
            </Text>
          </StyledReadMore>
        </Description>
      )}
    </Component>
  );
};

const StyledReadMore = styled(ReadMore)`
  font-size: ${({ _noPopupTabletSize }) =>
    _noPopupTabletSize ? '12px' : '14px'};
  line-height: ${({ _noPopupTabletSize }) =>
    _noPopupTabletSize ? '16px' : '20px'};
`;

const Component = styled.div.attrs({
  className: `${classnamePrefix}-info-component`
})`
  max-width: 980px;
  width: 100%;
  margin: 0 auto;

  display: ${({ _hasElements }) => !_hasElements && 'none'};

  & .${classnamePrefix}-share-window {
    left: ${({ _isInPopup, _isExtraSmallMobile }) =>
      !_isInPopup && _isExtraSmallMobile && '0'};
  }
`;

const Name = styled.div.attrs({
  className: `${classnamePrefix}-info-name`
})`
  margin-top: 20px;
  max-width: 600px;
  font-weight: 700;
  text-decoration: none !important;
  display: inline-block;
  font-size: ${({ _isInPopup, _fontSize }) => !_isInPopup && _fontSize};
  line-height: ${({ _isInPopup, _lineHeight }) => !_isInPopup && _lineHeight};
  color: ${({ _isInPopup, _textColor }) =>
    !_isInPopup ? _textColor : 'rgb(26, 46, 59)'};
`;

const Time = styled.div.attrs({
  className: `${classnamePrefix}-info-time`
})`
  margin-top: 8px;
  color: ${({ _isInPopup, _textColor }) =>
    !_isInPopup && _textColor
      ? changeColorAlpha(_textColor, -0.5)
      : 'rgba(26, 46, 59, 0.5)'};
  font-size: ${({ _noPopupTabletSize }) =>
    _noPopupTabletSize ? '12px' : '14px'};
  line-height: ${({ _noPopupTabletSize }) =>
    _noPopupTabletSize ? '16px' : '20px'};
`;

const Description = styled.div.attrs({
  className: `${classnamePrefix}-info-description`
})`
  margin-top: 12px;
  max-width: 600px;
  color: ${({ _isInPopup, _textColor }) =>
    !_isInPopup ? _textColor : 'rgb(26, 46, 59)'};
  font-size: ${({ _noPopupTabletSize }) =>
    _noPopupTabletSize ? '12px' : '14px'};
  line-height: ${({ _noPopupTabletSize }) =>
    _noPopupTabletSize ? '16px' : '20px'};
`;

const Text = styled.div.attrs({
  className: `${classnamePrefix}-info-text`
})`
  white-space: pre-wrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const Link = styled.a.attrs({
  className: `${classnamePrefix}-info-link`
})`
  color: currentColor;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;
